import React, { ChangeEvent, KeyboardEvent } from 'react';
import { CurrencyType } from '../../../types/model/vehicle.model';
import styles from './PriceInput.module.css';

interface PriceInputProps {
    value: number;
    currency: CurrencyType;
    onChange: (value: number) => void;
    onCurrencyChange: (currency: CurrencyType) => void;
    error?: string;
    disabled?: boolean;
    min?: number;
    placeholder?: string;
    id?: string;
    name?: string;
}

export const PriceInput: React.FC<PriceInputProps> = ({
    value,
    currency,
    onChange,
    onCurrencyChange,
    error,
    disabled = false,
    min = 0,
    placeholder = "0",
    id = "price",
    name = "price"
}) => {

    // Formatea el número con puntos para miles (ej: 1.000.000)
    const formatNumber = (num: number): string => {
        if (num === 0 && !value) return ""; // Caso especial inicial si queremos vacío, pero aquí value es numérico.
        // Si value es 0, mostramos "0" o podríamos mostrar "" si preferimos placeholder.
        // Asumiremos que 0 se muestra como "0".
        return new Intl.NumberFormat('es-AR').format(num);
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        // Eliminar puntos y cualquier caracter no numérico
        const rawValue = e.target.value.replace(/\./g, '').replace(/,/g, '');

        // Si está vacío, resetear a 0
        if (rawValue === '') {
            onChange(0);
            return;
        }

        // Verificar que solo sean números
        if (!/^\d+$/.test(rawValue)) {
            return;
        }

        // Parsear a entero (esto también remueve ceros a la izquierda automáticamente: parseInt('05') -> 5)
        const numValue = parseInt(rawValue, 10);

        if (!isNaN(numValue)) {
            onChange(numValue);
        }
    };

    const handleCurrencyChange = (e: ChangeEvent<HTMLSelectElement>) => {
        onCurrencyChange(e.target.value as CurrencyType);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // Permitir teclas de control: Backspace, Delete, Tab, Escape, Enter, Flechas
        if (
            ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
        ) {
            return;
        }

        // Permitir Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
            return;
        }

        // Bloquear todo lo que no sea número
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    return (
        <div className={styles.container}>
            <div className={`${styles.inputGroup} ${error ? styles.errorBorder : ''} ${disabled ? styles.disabled : ''}`}>
                <input
                    type="text"
                    id={id}
                    name={name}
                    value={value === 0 && placeholder !== "0" ? "" : formatNumber(value)}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={placeholder}
                    className={styles.input}
                    autoComplete="off"
                />
                <select
                    value={currency}
                    onChange={handleCurrencyChange}
                    disabled={disabled}
                    className={styles.select}
                >
                    <option value={CurrencyType.USD}>USD</option>
                    <option value={CurrencyType.ARS}>ARS</option>
                </select>
            </div>
            {error && <span className={styles.errorText}>{error}</span>}
        </div>
    );
};
